import * as Api from '@ViewModels';
import { AccountFilterCriteriaProperty } from '../../../../viewmodels/AdminViewModels';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';

export enum BillingCycle {
	All = 0,
	Monthly = 1,
	Quarterly = 3,
	Semiaunnual = 6,
	Annual = 12,
}

export enum SubscriptionLengthOptions {
	All = 'All',
	OneYear = '1 Year',
	TwoYears = '2 Years',
	ThreeYears = '3 Years',
	FourYears = '4 Years',
}

export const SubscriptionLengthOptionCriteriaMap: Record<SubscriptionLengthOptions, string> = {
	[SubscriptionLengthOptions.All]: 'All',
	[SubscriptionLengthOptions.OneYear]: '1',
	[SubscriptionLengthOptions.TwoYears]: '2',
	[SubscriptionLengthOptions.ThreeYears]: '3',
	[SubscriptionLengthOptions.FourYears]: '4',
};

export enum IIndustryOptions {
	All = '',
	Insurance = Api.Industry.Insurance,
	RealEstate = Api.Industry.RealEstate,
	Financial = Api.Industry.Financial,
	Mortgage = Api.Industry.Mortgage,
	Legal = Api.Industry.Legal,
	Accounting = Api.Industry.Accounting,
	NonProfit = Api.Industry.NonProfit,
	HomeMaintenance = Api.Industry.HomeMaintenance,
	Dental = Api.Industry.Dental,
	Chiropractic = Api.Industry.Chiropractic,
	Miscellaneous = Api.Industry.Miscellaneous,
}

export enum AdminMonthlyReportsWebsiteOptions {
	All = '',
	WithWebsites = 'WithWebsites',
	WithoutWebsites = 'WithoutWebsites',
}

export interface IWebsiteCriteria {
	property: AccountFilterCriteriaProperty.Websites;
	value: boolean | null;
}

export const SUBSCRIPTION_LENGTH_OPTIONS: ISelectOption<SubscriptionLengthOptions>[] = [
	{
		dataContext: SubscriptionLengthOptions.All,
		forceSelectAll: true,
		id: `all-renewals`,
		text: 'Show all renewals',
		type: 'default',
	},
	{
		dataContext: SubscriptionLengthOptions.OneYear,
		id: `one-year-renewal-option`,
		text: 'First year renewals',
		type: 'default',
	},
	{
		dataContext: SubscriptionLengthOptions.TwoYears,
		id: `second-year-renewal-option`,
		text: 'Second year renewals',
		type: 'default',
	},
	{
		dataContext: SubscriptionLengthOptions.ThreeYears,
		id: `third-year-renewal-option`,
		text: 'Third year renewals',
		type: 'default',
	},
	{
		dataContext: SubscriptionLengthOptions.FourYears,
		id: `fourth-year-renewal-option`,
		text: 'Fourth year renewals',
		type: 'default',
	},
] as const;

export const INDUSTRY_OPTIONS: ISelectOption<string>[] = [
	{
		dataContext: '',
		forceSelectAll: true,
		id: `show-all`,
		text: 'Show all Industries',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Insurance,
		id: `insurance-option`,
		text: 'Insurance',
		type: 'default',
	},
	{
		dataContext: Api.Industry.RealEstate,
		id: `real-estate-option`,
		text: 'Real Estate',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Financial,
		id: `financial-option`,
		text: 'Financial',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Legal,
		id: `legal-option`,
		text: 'Legal',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Miscellaneous,
		id: `miscellaneous-option`,
		text: 'Misc',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Mortgage,
		id: `mortgage-option`,
		text: 'Mortgage',
		type: 'default',
	},
	{
		dataContext: Api.Industry.NonProfit,
		id: `non-profit-option`,
		text: 'Non-Profit',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Accounting,
		id: `accounting-option`,
		text: 'Accounting',
		type: 'default',
	},
	{
		dataContext: Api.Industry.HomeMaintenance,
		id: `homemaintenance-option`,
		text: 'Home Maintenance',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Dental,
		id: `dental-option`,
		text: 'Dental',
		type: 'default',
	},
	{
		dataContext: Api.Industry.Chiropractic,
		id: `chiropractic-option`,
		text: 'Chiropractic',
		type: 'default',
	},
] as const;

export const BILLING_CYCLE_OPTIONS: ISelectOption<BillingCycle>[] = [
	{
		dataContext: BillingCycle.All,
		forceSelectAll: true,
		id: 'billing-filter-option-all',
		text: 'Show all',
		type: 'checkbox',
	},
	{
		dataContext: BillingCycle.Monthly,
		id: 'billing-filter-option-monthly',
		text: 'Renews every month',
		type: 'checkbox',
	},
	{
		dataContext: BillingCycle.Quarterly,
		id: 'billing-filter-option-quarterly',
		text: 'Renews every 3 months',
		type: 'checkbox',
	},
	{
		dataContext: BillingCycle.Semiaunnual,
		id: 'billing-filter-option-semiannually',
		text: 'Renews every 6 months',
		type: 'checkbox',
	},
	{
		dataContext: BillingCycle.Annual,
		id: 'billing-filter-option-annually',
		text: 'Renews every year',
		type: 'checkbox',
	},
] as const;

export const WEBSITE_OPTIONS: ISelectOption<AdminMonthlyReportsWebsiteOptions>[] = [
	{
		dataContext: AdminMonthlyReportsWebsiteOptions.All,
		id: 'website-filter-option-all',
		text: 'No website filter',
	},
	{
		dataContext: AdminMonthlyReportsWebsiteOptions.WithWebsites,
		id: 'website-filter-option-accounts-with-websites',
		text: 'Accounts with websites',
	},
	{
		dataContext: AdminMonthlyReportsWebsiteOptions.WithoutWebsites,
		id: 'website-filter-option-accounts-without-websites',
		text: 'Accounts without websites',
	},
];

export enum BillingStatusOptions {
	All = 'All',
	Active = 'Active',
	Cancelled = 'Cancelled',
	PastDue = 'PastDue',
}

export const BILLING_STATUS_OPTIONS: ISelectOption<BillingStatusOptions>[] = [
	{
		dataContext: BillingStatusOptions.All,
		forceSelectAll: true,
		id: 'billing-status-filter-option-all',
		text: 'Show all statuses',
		type: 'default',
	},
	{
		dataContext: BillingStatusOptions.Active,
		id: 'billing-status-filter-option-active',
		text: 'Active',
		type: 'default',
	},
	{
		dataContext: BillingStatusOptions.Cancelled,
		id: 'billing-status-filter-option-cancelled',
		text: 'Cancelled',
		type: 'default',
	},
	{
		dataContext: BillingStatusOptions.PastDue,
		id: 'billing-status-filter-option-past-due',
		text: 'Past Due',
		type: 'default',
	},
] as const;
